import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Sparkles, Download, RefreshCw } from "lucide-react";
import { BuyButton } from "./BuyButton";
import { IMAGES } from "./constants";

const LINES = [
  { text: "The upstander training", accent: false },
  { text: "we deliver for $10,000.", accent: false },
  { text: "Yours to deliver for $249.", accent: true },
];

const MaskedLine = ({ children, index }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ delay: 0.25 + index * 0.14, duration: 0.95, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} id="top" className="relative overflow-hidden pt-32 lg:pt-40" data-testid="hero-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 pb-24 lg:grid-cols-12 lg:px-10 lg:pb-32">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="mb-8 inline-flex items-center gap-2 rounded-full border border-navy/15 bg-white/60 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-navy/70"
            data-testid="hero-eyebrow"
          >
            <Sparkles className="h-3.5 w-3.5 text-terra" aria-hidden="true" />
            Train-the-Trainer Toolkit · White-Label
          </motion.div>

          <h1 className="font-display text-4xl font-extrabold leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            {LINES.map((line, i) => (
              <MaskedLine key={line.text} index={i}>
                <span className={line.accent ? "text-terra" : ""}>{line.text}</span>
              </MaskedLine>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl text-base leading-relaxed text-navy/70 md:text-lg"
            data-testid="hero-subcopy"
          >
            Building a Speak-Up Culture™ is Civility Partners' most popular training
            program, and we're giving it to you — training slides, a facilitator's guide,
            learner handouts, and video coaching from our expert facilitators. Download
            it, brand it as your own, and deliver it as often as you like.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <BuyButton testId="hero-buy-button" size="lg">Get the Toolkit — $249</BuyButton>
            <a
              href="#inside"
              data-testid="hero-see-inside-link"
              className="inline-flex items-center gap-2 rounded-full border border-navy/25 px-7 py-4 text-base font-semibold text-navy transition-[background-color,color,transform] duration-300 hover:scale-[1.03] hover:bg-navy hover:text-cream"
            >
              See what's inside
            </a>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-4 text-sm text-navy/60"
            data-testid="hero-stats"
          >
            <li className="flex items-center gap-2"><Download className="h-4 w-4 text-terra" aria-hidden="true" /> Instant download, ready to brand</li>
            <li className="flex items-center gap-2"><RefreshCw className="h-4 w-4 text-terra" aria-hidden="true" /> One-time purchase, unlimited reuse</li>
          </motion.ul>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="overflow-hidden rounded-[2rem] border border-navy/10 shadow-[0_40px_80px_-30px_rgba(15,23,42,0.35)]">
              <motion.img
                src={IMAGES.hero}
                alt="Catherine Mattice facilitating a workplace culture training on stage"
                style={{ y: imgY, scale: 1.15 }}
                className="h-[420px] w-full object-cover object-[50%_25%] lg:h-[520px]"
                data-testid="hero-image"
              />
            </div>
            <motion.div
              style={{ y: cardY }}
              className="absolute -bottom-8 -left-4 rounded-2xl border border-navy/10 bg-white/90 p-5 shadow-xl backdrop-blur-md sm:-left-8"
              data-testid="hero-price-card"
            >
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-navy/50">One-time purchase</p>
              <p className="mt-1 font-display text-3xl font-extrabold text-navy">
                $249 <span className="text-sm font-medium text-navy/50 line-through">$10,000 delivered</span>
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
